import { useMemo } from "react";

import type { FilterDefinitionType } from "@/components/@types";
import type { ReportSource } from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { sharedFilterDefinitionsToRecord } from "@/app/share/report/sharedFilterDefinitions";
import { SHARED_LAYOUT_GLOBAL_FILTER_KEYS } from "@/app/share/report/sharedReportFilters";
import type {
  SharedFilterDefinition,
  SharedViewWidgetShell,
} from "@/app/share/report/sharedReportTypes";
import { Queries } from "@/constants";
import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";

function toFilterDefinitionRecord(
  definitions: Pick<
    SharedFilterDefinition,
    "key" | "type" | "name" | "operators" | "valueOptions"
  >[],
): Record<string, FilterDefinitionType> {
  return sharedFilterDefinitionsToRecord(
    definitions.map((definition) => ({
      key: definition.key,
      type: definition.type,
      name: definition.name,
      operators: definition.operators ?? [],
      valueOptions: definition.valueOptions,
    })),
  );
}

export function useLayoutReportFilterDefinitions(): Record<
  string,
  FilterDefinitionType
> {
  const { data } = useQuery(Queries.REPORT_FILTER_DEFINITIONS_QUERY, {
    variables: { construct: null },
    fetchPolicy: "cache-first",
  });

  return useMemo(
    () => toFilterDefinitionRecord(data?.reportFilterDefinitions ?? []),
    [data?.reportFilterDefinitions],
  );
}

export function useWidgetReportFilterDefinitions(
  source: string | undefined,
): Record<string, FilterDefinitionType> {
  const { data } = useQuery(Queries.REPORT_FILTER_DEFINITIONS_QUERY, {
    variables: { construct: source ?? null },
    skip: !source,
    fetchPolicy: "cache-first",
  });

  return useMemo(
    () => toFilterDefinitionRecord(data?.reportFilterDefinitions ?? []),
    [data?.reportFilterDefinitions],
  );
}

export function useSharedWidgetFilterDefinitions(
  widget: { source?: string | null; filterDefinitions: SharedFilterDefinition[] },
): Record<string, FilterDefinitionType> {
  const catalogDefinitions = useWidgetReportFilterDefinitions(widget.source ?? undefined);

  return useMemo(() => {
    const fromApi = sharedFilterDefinitionsToRecord(widget.filterDefinitions);
    return Object.fromEntries(
      Object.entries({ ...catalogDefinitions, ...fromApi }).filter(
        ([key]) => !SHARED_LAYOUT_GLOBAL_FILTER_KEYS.has(key),
      ),
    );
  }, [catalogDefinitions, widget.source, widget.filterDefinitions]);
}

/**
 * What a widget can be of, as the server publishes it.
 *
 * The browser used to keep its own catalogue of constructs and of what each
 * could be drawn as; this is the server's, so the two cannot drift.
 */
export function useReportSourceCatalog(): ReportSource[] {
  const { data } = useQuery(Queries.REPORT_SOURCE_CATALOG_QUERY, {
    fetchPolicy: "cache-first",
  });

  return useMemo(
    () =>
      (data?.reportSourceCatalog?.data ?? []).map((source) => ({
        name: source.name,
        label: source.label,
        presentations: source.presentations as ReportSource["presentations"],
        filters: source.filters,
        isSummarized: source.isSummarized,
        fields: source.fields.map((field) => ({
          name: field.name,
          label: field.label,
          kind: field.kind,
          typename: field.typename,
          isGroupable: field.isGroupable,
          isMeasurable: field.isMeasurable,
          takesInterval: field.takesInterval,
        })),
      })),
    [data?.reportSourceCatalog?.data],
  );
}
